import React, { FC, InputHTMLAttributes, useId } from "react";
import classnames from "classnames";
import DynamicHeroIcon from "./DynamicHeroIcon";
import type { HeroIcons } from "./DynamicHeroIcon";
interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  inputSize?: "sm" | "base" | "lg";
  error?: string;
  helperText?: string;
  leftIcon?: HeroIcons;
  clearButton?: boolean;
  fullWidth?: boolean;
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  function InputField(
    {
      label,
      helperText,
      error,
      leftIcon,
      inputSize = "base",
      clearButton,
      required,
      fullWidth,
      ...rest
    },
    ref
  ): JSX.Element {
    const elemId = useId();
    const hasError = error ? true : false;
    return (
      <>
        {label ? (
          <label
            htmlFor={elemId}
            className={classnames(
              "mb-2 block text-sm font-medium",
              { "text-gray-900 dark:text-white": !hasError },
              { "text-red-700 dark:text-red-500": hasError }
            )}
          >
            {label}{" "}
            {required ? null : (
              <span className="text-sm text-gray-500 dark:text-gray-600">
                (optional)
              </span>
            )}
          </label>
        ) : null}

        <div className={classnames("relative", { "w-full": fullWidth })}>
          {leftIcon ? (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <DynamicHeroIcon
                icon={leftIcon}
                className={classnames("text-gray-500 dark:text-gray-400", {
                  "h-4 w-4": inputSize === "sm" || inputSize === "base",
                  "h-5 w-5": inputSize === "lg",
                })}
              />
            </div>
          ) : null}
          <input
            id={elemId}
            ref={ref}
            className={classnames(
              "block w-full rounded-lg border px-4 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-900 dark:focus:outline-none  dark:disabled:cursor-not-allowed dark:disabled:bg-gray-700",
              {
                "border-gray-300 bg-gray-50 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500":
                  !hasError,
              },
              {
                "border-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 dark:border-red-500 dark:bg-red-100 dark:text-red-500  dark:placeholder-red-500 dark:focus:outline-none dark:focus:ring-1":
                  hasError,
              },
              {
                "pl-10": leftIcon,
              },
              {
                "pr-10": clearButton,
              },
              { "py-2 text-sm sm:text-xs": inputSize === "sm" },
              { "py-3 text-sm": inputSize === "base" },
              { "sm:text-md py-4 text-base": inputSize === "lg" }
            )}
            {...rest}
          />
          {clearButton ? (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <DynamicHeroIcon
                icon={"XMarkIcon"}
                className={"h-3 w-3 text-gray-500 dark:text-gray-400"}
              />
            </button>
          ) : null}
        </div>
        {error ? (
          <>
            <p className="mt-2 text-sm text-red-600 dark:text-red-500">
              {error}
            </p>
          </>
        ) : (
          <>
            {helperText ? (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {helperText}
              </p>
            ) : null}
          </>
        )}
      </>
    );
  }
);

export default InputField;
